import React, { useState, useCallback } from 'react';
import { Formik, Form, Field } from 'formik';
import {
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useApiCaller from 'hooks/apiCaller';
import debounce from 'lodash.debounce';

const validationSchema = Yup.object({
  employees: Yup.array()
    .min(1, 'Please select at least one employee')
    .required('Please select an employee'),
});

const bulkValidationSchema = Yup.object({
  employeeIds: Yup.string().required('Please enter employee IDs'),
});

const AddEmployeeByIDForm = ({ onSubmit }) => {
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [bulkEmployeeIds, setBulkEmployeeIds] = useState('');
  const [verifiedEmployees, setVerifiedEmployees] = useState([]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const apiCaller = useApiCaller();

  const fetchEmployees = async (search) => {
    if (search.trim().length > 0) {
      setLoadingEmployees(true);
      try {
        const data = { searchString: search };
        const response = await apiCaller.post('/user/getEmployee', data);
        if (response) {
          setEmployees(response?.data?.data?.employee || []);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
        toast.error('Failed to fetch employees');
      } finally {
        setLoadingEmployees(false);
      }
    } else {
      setEmployees([]);
    }
  };

  const debouncedFetchEmployees = useCallback(
    debounce((search) => {
      fetchEmployees(search);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    setSearchString(event.target.value);
    debouncedFetchEmployees(event.target.value);
  };

  const handleBulkModeToggle = () => {
    setIsBulkMode(!isBulkMode);
    setBulkEmployeeIds('');
    setVerifiedEmployees([]);
    setIsVerified(false);
  };

  const verifyEmployeeIds = async () => {
    console.log('verifyEmployeeIds called with bulkEmployeeIds:', bulkEmployeeIds);

    if (!bulkEmployeeIds.trim()) {
      toast.error('Please enter employee IDs');
      return;
    }

    setIsVerifying(true);
    try {
      // Split by comma or space and clean up
      const employeeIds = bulkEmployeeIds
        .split(/[,\s]+/)
        .map((id) => id.trim())
        .filter((id) => id.length > 0);

      console.log('Parsed employee IDs:', employeeIds);

      if (employeeIds.length === 0) {
        toast.error('Please enter valid employee IDs');
        return;
      }

      const verifiedEmployeesList = [];
      const invalidIds = [];

      // Verify each employee ID
      for (const employeeId of employeeIds) {
        try {
          const data = { searchString: employeeId };
          console.log(`Making API call for employee ID: ${employeeId} with data:`, data);

          const response = await apiCaller.post('/user/getEmployee', data);
          const employees = response?.data?.data?.employee || [];

          console.log(`Searching for employee ID: ${employeeId}`);
          console.log(`API returned ${employees.length} employees:`, employees);

          // Check for exact match first
          const foundEmployee = employees.find((emp) => {
            const empId = String(emp.employeeId || emp.employee_id || emp.id || '');
            const searchId = String(employeeId);
            return empId === searchId;
          });

          if (foundEmployee) {
            console.log(`Found exact match for ${employeeId}:`, foundEmployee);
            verifiedEmployeesList.push(foundEmployee);
          } else {
            // If no exact match, check if the search returned any results
            if (employees.length > 0) {
              console.log(
                `No exact match for ${employeeId}, but found ${employees.length} results. Using first result:`,
                employees[0]
              );
              verifiedEmployeesList.push(employees[0]);
            } else {
              console.log(`No results found for employee ID: ${employeeId}`);
              invalidIds.push(employeeId);
            }
          }
        } catch (error) {
          console.error(`Error verifying employee ID ${employeeId}:`, error);
          invalidIds.push(employeeId);
        }
      }

      console.log('Final verified employees list:', verifiedEmployeesList);
      console.log('Invalid IDs:', invalidIds);

      if (invalidIds.length > 0) {
        toast.error(`Invalid employee IDs: ${invalidIds.join(', ')}`);
        setVerifiedEmployees([]);
        setIsVerified(false);
      } else {
        setVerifiedEmployees(verifiedEmployeesList);
        setIsVerified(true);
        toast.success(`Successfully verified ${verifiedEmployeesList.length} employee(s)`);
      }
    } catch (error) {
      console.error('Error verifying employees:', error);
      toast.error('Failed to verify employees');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = (values) => {
    console.log('handleSubmit called with values:', values);
    console.log('isBulkMode:', isBulkMode);
    console.log('verifiedEmployees:', verifiedEmployees);

    try {
      let newEmployees;

      if (isBulkMode) {
        newEmployees = verifiedEmployees.map(
          (emp) => `${emp.employeeId}/${emp.employeeName}/${emp.funcTitleName}/${emp.dateOfJoining}`
        );
        console.log('Bulk mode - newEmployees:', newEmployees);
      } else {
        newEmployees = values.employees.map(
          (emp) => `${emp.employeeId}/${emp.employeeName}/${emp.funcTitleName}/${emp.dateOfJoining}`
        );
        console.log('Single mode - newEmployees:', newEmployees);
      }

      console.log('Submitting employees:', newEmployees);
      onSubmit(newEmployees);

      // Reset bulk mode state after successful submission
      if (isBulkMode) {
        setBulkEmployeeIds('');
        setVerifiedEmployees([]);
        setIsVerified(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to add employees');
    }
  };

  return (
    <>
      <Box
        sx={{
          p: 3,
          minWidth: 500,
          maxWidth: 600,
          '& .MuiAutocomplete-root': {
            width: '100%',
            mb: 2,
          },
          '& .MuiAutocomplete-inputRoot': {
            padding: '4px 8px',
          },
          '& .MuiChip-root': {
            margin: '2px',
          },
          '& .MuiFormHelperText-root': {
            ml: 0,
          },
        }}
      >
        <Typography variant="h6" gutterBottom>
          Search and Add Employees
        </Typography>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch checked={isBulkMode} onChange={handleBulkModeToggle} color="primary" />
            }
            label={
              <Typography variant="body2">
                {isBulkMode ? 'Bulk Add Mode' : 'Single Add Mode'}
              </Typography>
            }
          />
          <Typography variant="caption" color="textSecondary" display="block">
            {isBulkMode
              ? 'Paste multiple employee IDs separated by commas or spaces'
              : 'Search and select employees one by one'}
          </Typography>
        </Box>

        {isBulkMode ? (
          <Formik
            initialValues={{ employeeIds: bulkEmployeeIds }}
            validationSchema={bulkValidationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ errors, touched, values, setFieldValue, handleSubmit: formikHandleSubmit }) => {
              console.log('Formik values:', values);
              console.log('Formik errors:', errors);
              console.log('Formik touched:', touched);
              return (
                <Form>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                    placeholder="Enter employee IDs separated by commas or spaces (e.g., EMP001, EMP002 EMP003)"
                    value={bulkEmployeeIds}
                    onChange={(e) => {
                      setBulkEmployeeIds(e.target.value);
                      setFieldValue('employeeIds', e.target.value);
                    }}
                    sx={{ mb: 2 }}
                  />

                  <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={verifyEmployeeIds}
                    disabled={isVerifying || !bulkEmployeeIds.trim()}
                    sx={{ mb: 2 }}
                  >
                    {isVerifying ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Verifying...
                      </>
                    ) : (
                      'Verify Employee IDs'
                    )}
                  </Button>

                  {isVerified && verifiedEmployees.length > 0 && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                      <Typography variant="body2" color="success.contrastText">
                        Verified {verifiedEmployees.length} employee(s):
                      </Typography>
                      {verifiedEmployees.map((emp, index) => (
                        <Typography
                          key={emp.employeeId}
                          variant="body2"
                          color="success.contrastText"
                        >
                          {index + 1}. {emp.employeeId} - {emp.employeeName}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={!isVerified || verifiedEmployees.length === 0}
                    onClick={() => {
                      console.log('Submit button clicked');
                      console.log('Form values:', values);
                      console.log('Form errors:', errors);
                      console.log('Is verified:', isVerified);
                      console.log('Verified employees:', verifiedEmployees);
                    }}
                    sx={{
                      py: 1,
                      fontSize: '1rem',
                    }}
                  >
                    Add{' '}
                    {verifiedEmployees.length > 0
                      ? `${verifiedEmployees.length} Employee(s)`
                      : 'Employees'}
                  </Button>
                </Form>
              );
            }}
          </Formik>
        ) : (
          <Formik
            initialValues={{ employees: [] }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, setFieldValue }) => (
              <Form>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Search by employee ID or name to add them to the training
                </Typography>

                <Autocomplete
                  multiple
                  size="small"
                  value={values.employees}
                  options={employees}
                  getOptionLabel={(option) => `${option.employeeId}-${option.employeeName}`}
                  onChange={(event, newValue) => setFieldValue('employees', newValue)}
                  filterSelectedOptions
                  loading={loadingEmployees}
                  isOptionEqualToValue={(option, value) => option.employeeId === value.employeeId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Search by Employee ID or Name"
                      error={touched.employees && !!errors.employees}
                      helperText={touched.employees && errors.employees}
                      onChange={handleSearchChange}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingEmployees ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{
                    mt: 2,
                    py: 1,
                    fontSize: '1rem',
                  }}
                >
                  Add Selected Employees
                </Button>
              </Form>
            )}
          </Formik>
        )}
      </Box>
    </>
  );
};

export default AddEmployeeByIDForm;
